// Small pure pieces of the model stage, kept out of the component so
// they can be tested without a WebGL context.
import { Box3, DoubleSide, Matrix4, MeshBasicMaterial, ShaderMaterial, Vector3 } from "three";

// Which date a status change carries. Work already done is dated "as
// of" (today or earlier); a projection is dated when it is EXPECTED,
// which is usually ahead - so it has its own field rather than a past
// date pretending to be a plan.
export function dateForStatus(status, statusDate, projectedDate) {
  if (status === "projected") return projectedDate || statusDate || undefined;
  return statusDate || undefined;
}

// The tracked elements nobody has given a status: shown as ghosts by
// "Clear untracked", so a site reads as "what is left to do" rather
// than a field of one colour.
export function ghostSet(scopeUids, touchedByUid) {
  const out = [];
  for (const uid of scopeUids || []) {
    if (!touchedByUid || !touchedByUid.has(uid)) out.push(uid);
  }
  return out;
}

// Above this many scope elements the model does not paint the untouched
// part of the scope `projected`: a colour on a quarter of a million
// elements asserting "nobody has started" costs more than it tells.
// Elements WITH a status are always painted, whatever the size.
export const SCOPE_PAINT_MAX = 150000;

// What the model paints, uid -> status. Every element with a status
// paints in its own step - in the ticked scope or not, because a status
// is somebody saying "this is being tracked" and unticking its category
// must not make that disappear. The untouched rest of the scope paints
// `projected`, up to SCOPE_PAINT_MAX.
export function paintMap(scopeUids, statusByUid, paintMax = SCOPE_PAINT_MAX) {
  const map = new Map();
  if (scopeUids && scopeUids.length && scopeUids.length <= paintMax) {
    for (const uid of scopeUids) map.set(uid, "projected");
  }
  if (statusByUid) statusByUid.forEach((status, uid) => map.set(uid, status));
  return map;
}

// What "Only tracked" shows: the ticked scope AND every element with a
// status - the same set the model paints, so the switch never hides
// something that is coloured.
export function trackedUids(scopeUids, touchedByUid) {
  const out = new Set(scopeUids || []);
  if (touchedByUid) touchedByUid.forEach((_, uid) => out.add(uid));
  return [...out];
}

// The isolation the canvas applies, or null for "show everything".
// A page's cut narrows; "Only tracked" narrows to trackedUids; the
// legend's "Not tracked" off narrows to elements with a status. Each
// applies inside the ones before it, and an empty result is an empty
// view, never the whole model.
export function isolationFor({ isolateUids = null, onlyTracked = false, scopeUids = null, hideUntracked = false, touchedByUid = null }) {
  let uids = isolateUids && isolateUids.length ? isolateUids : null;
  const narrow = (set) => {
    const cut = uids ? new Set(uids) : null;
    const next = cut ? set.filter((u) => cut.has(u)) : set;
    return next.length ? next : ["__nothing__"];
  };
  if (onlyTracked) {
    const tracked = trackedUids(scopeUids, touchedByUid);
    if (tracked.length) uids = narrow(tracked);
  }
  if (hideUntracked) uids = narrow(touchedByUid ? [...touchedByUid.keys()] : []);
  return uids;
}

// The world box around a set of elements, from their live pool slots.
// `ownerOf(uid)` answers { mesh, index } for a materialised element and
// null otherwise; an element not yet streamed simply does not count.
// Grown a little so a single fitting is not framed to a point.
const _m = new Matrix4();
const _p = new Vector3();
export function boxForUids(ownerOf, uids, { minSize = 12, limit = 60000 } = {}) {
  const box = new Box3();
  let n = 0;
  for (const uid of uids || []) {
    if (n++ >= limit) break;
    const o = ownerOf(uid);
    if (!o || !o.mesh || typeof o.index !== "number" || typeof o.mesh.getMatrixAt !== "function") continue;
    o.mesh.getMatrixAt(o.index, _m);
    _p.setFromMatrixPosition(_m);
    if (o.mesh.matrixWorld) _p.applyMatrix4(o.mesh.matrixWorld);
    if (!Number.isFinite(_p.x) || !Number.isFinite(_p.y) || !Number.isFinite(_p.z)) continue;
    // A zeroed slot (hidden instance) sits at the origin: not a position.
    if (_p.x === 0 && _p.y === 0 && _p.z === 0) continue;
    box.expandByPoint(_p);
  }
  if (box.isEmpty()) return null;
  const size = box.getSize(new Vector3());
  const pad = new Vector3(
    Math.max(0, (minSize - size.x) / 2), Math.max(0, (minSize - size.y) / 2), Math.max(0, (minSize - size.z) / 2),
  );
  box.min.sub(pad); box.max.add(pad);
  return box;
}

// The sheet as a halftone: the drawing's tones become a screen of dots
// whose size follows the darkness, so a plan reads as a print rather
// than a photograph under the model, and the model's own colours stand
// off it. Screen-space dots, so the pattern does not scale with the
// sheet.
export const HALFTONE_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;
export const HALFTONE_FRAGMENT = `
  uniform sampler2D map;
  uniform float opacity;
  uniform float cell;
  varying vec2 vUv;
  void main() {
    vec4 c = texture2D(map, vUv);
    float lum = dot(c.rgb, vec3(0.299, 0.587, 0.114));
    vec2 g = fract(gl_FragCoord.xy / cell) - 0.5;
    float d = length(g) * 2.0;
    float r = (1.0 - lum) * 1.15;
    float ink = 1.0 - smoothstep(r - 0.12, r + 0.12, d);
    vec3 col = mix(vec3(1.0), vec3(0.12), ink);
    gl_FragColor = vec4(col, opacity * c.a);
  }
`;
export function buildSheetMaterial(texture, opacity, halftone) {
  const common = { transparent: true, side: DoubleSide, depthWrite: false };
  if (halftone) {
    return new ShaderMaterial({
      ...common,
      uniforms: { map: { value: texture }, opacity: { value: Number(opacity) ?? 0.85 }, cell: { value: 6.0 } },
      vertexShader: HALFTONE_VERTEX,
      fragmentShader: HALFTONE_FRAGMENT,
    });
  }
  return new MeshBasicMaterial({ ...common, map: texture, opacity: Number(opacity) ?? 0.85 });
}
export function setSheetOpacity(material, opacity) {
  if (!material) return;
  const v = Number(opacity) ?? 0.85;
  if (material.uniforms && material.uniforms.opacity) material.uniforms.opacity.value = v;
  else material.opacity = v;
  material.needsUpdate = true;
}
